import { useMemo, useCallback } from 'react';

import { DataTable, type TableColumn } from '@/src/ui';
import type { CompletionStatusReportSummaryDTO } from '../models/completionStatusReportDTOs';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

type GroupBy = 'assessmentClass' | 'assessment';

export interface OpenDetailsPayload {
    examAssignmentId: number;
    examName?: string;
    className?: string;
    classId?: number;
    isByExam: boolean;
    examId?: number;
}

interface StatusDataTableProps {
    data: Array<CompletionStatusReportSummaryDTO>;
    groupBy: GroupBy;
    onOpenDetails: (payload: OpenDetailsPayload) => void;
    selectedClassId?: number;
    pdfFileName?: string | (() => string);
}

// Status report table using shared DataTable
export default function StatusDataTable(props: StatusDataTableProps) {
    const { data, groupBy, selectedClassId, onOpenDetails, pdfFileName } = props;

    const buildPayload = useCallback(
        (row: CompletionStatusReportSummaryDTO): OpenDetailsPayload => {
            const isByExam = groupBy === 'assessment';
            return {
                examAssignmentId: row.examAssignmentId!,
                examName: row.examName ?? undefined,
                className: row.className ?? undefined,
                classId: isByExam ? selectedClassId || undefined : (row.classId ?? undefined),
                isByExam,
                examId: (row as any).examId ?? undefined,
            };
        },
        [groupBy, selectedClassId]
    );

    // Check if details exist
    const hasDetails = (row: CompletionStatusReportSummaryDTO) => row.examAssignmentId != null;

    // Define table columns
    const columns = useMemo<TableColumn[]>(() => {
        const numSort = (a: number, b: number, dir: 'asc' | 'desc') =>
            dir === 'asc' ? a - b : b - a;

        return [
            {
                dataKey: 'examName',
                header: 'Assessment',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: CompletionStatusReportSummaryDTO) => {
                    return (
                        <a href={`/reports/questionList/${row.examId}`} target="_blank">
                            {row.examName}
                        </a>
                    );
                },
            },
            {
                dataKey: 'className',
                header: 'Class Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                hideColumn: groupBy === 'assessment',
                formatter: (row: CompletionStatusReportSummaryDTO) =>
                    hasDetails(row) ? (
                        <a
                            href="#"
                            title="View student details"
                            onClick={(e) => {
                                e.preventDefault();
                                onOpenDetails(buildPayload(row));
                            }}
                        >
                            {row.className}
                        </a>
                    ) : (
                        row.className
                    ),
            },
            {
                dataKey: 'studentsAssigned',
                header: 'Students Assigned',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsAssigned ?? 0),
            },
            {
                dataKey: 'studentsCompleted',
                header: 'Students Completed',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsCompleted ?? 0),
            },
            {
                dataKey: 'studentsIncomplete',
                header: 'Students Incomplete',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsIncomplete ?? 0),
            },
            {
                dataKey: 'studentsPassed',
                header: 'Students Passed',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsPassed ?? 0),
            },
        ];
    }, [groupBy, buildPayload, onOpenDetails]);

    // Render table
    return (
        <DataTable
            data={data}
            columns={columns}
            tableName="CompletionStatusReport"
            rowsPerPageInitial={50}
            enableExportCSV
            enableExportExcel
            enableExportPDF
            pdfFileName={pdfFileName}
            alternatingRowColor
            defaultSortColumn="examName"
            defaultSortDirection="asc"
        />
    );
}
